import React from 'react'
import { Teacher } from '../../providers/context'
import { Loader } from '../../components/simple/loader'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../request'
import { IconSwap } from '../../icons/library'
import { Button, ButtonStyle } from '../../components/simple/button'
import { useLocalStorageState } from '../../hooks/hooks'
import { Tab, Tabs } from '../../layouts/components/tabs'
import { NoMobileOverlay } from '../../layouts/components/noMobile'
import { TeacherConferenceData, isLocationEmpty } from './components/conferenceComponents'
import { EditLocationModal } from './components/editLocation'
import { Logo } from '../../components/simple/logo'
import { Header } from '../../layouts/components/header'
import { IconHelp } from '../../icons/help'
import { ScheduleTab } from './tabs/scheduleTab'
import { InviteTab } from './tabs/inviteTab'
import { AppointmentsTab } from './tabs/appointmentsTab'
import { TeacherInfoTab } from './tabs/teacherInfoTab'
import { StudentInfoTab } from './tabs/studentInfoTab'
import { StatsTab } from './tabs/statsTab'

export const PTCConferencePage: React.FC<{ user: Teacher; id: number }> = ({ user, id }) => {
  const [viewAsAdminRaw, setViewAsAdmin] = useLocalStorageState(`conferencePerspective${id}-${user.id}`, user.PTCAdmin)
  const viewAsAdmin = viewAsAdminRaw && user.PTCAdmin

  const { data: conference, reload: reloadConference } = useRequest<TeacherConferenceData>(
    `/frontend_api/getConference`,
    React.useMemo(
      () => ({
        id,
        viewAsAdmin,
      }),
      [id, viewAsAdmin]
    )
  )
  const [editingLocation, setEditingLocation] = React.useState(false)
  React.useEffect(() => {
    if (conference && !viewAsAdmin && isLocationEmpty(conference?.yourLocation, conference.virtualOrInPerson))
      setEditingLocation(true)
  }, [conference])

  const activateConference = (id: number, active: boolean) => {
    makeRequest('/frontend_api/activateConference', {
      id,
      active,
    })
      .then(reloadConference)
      .catch(showAxiosErrorAsToast)
  }

  return (
    <div className="flex h-screen max-h-screen flex-col">
      <Header>
        <Logo size="sm" link="/" showName showTM />
        <a
          href="https://reachmyteach.zendesk.com/hc/en-us/articles/39929820557209-Scheduling-Parent-Teacher-Conferences-on-Behalf-of-Families-as-a-PTC-Admin"
          target="_blank"
          className="ml-auto mr-2 text-gray-200 hover:text-gray-300"
        >
          <IconHelp size="xl" />
        </a>
      </Header>
      <NoMobileOverlay />
      <div className="flex min-h-0 flex-grow flex-col overflow-hidden p-6">
        {conference ? (
          <>
            <EditLocationModal
              user={user}
              conference={conference}
              reloadConference={reloadConference}
              open={editingLocation}
              onClose={() => setEditingLocation(false)}
            />
            <div className="flex items-center gap-2 self-stretch">
              <div className="mr-auto text-2xl font-bold">{conference.name}</div>
              {user.PTCAdmin && (
                <Button buttonStyle={ButtonStyle.dark} onClick={() => setViewAsAdmin((v) => !v)}>
                  <IconSwap />
                  Viewing as {viewAsAdmin ? 'admin' : 'yourself'}
                </Button>
              )}
              {user.PTCAdmin &&
                (conference.active ? (
                  <Button
                    buttonStyle={ButtonStyle.light}
                    tooltip="Disabling this conference turns off invites and sign-ups. This can be undone."
                    onClick={() => activateConference(conference.id, false)}
                  >
                    Turn Off Signups
                  </Button>
                ) : (
                  <Button
                    buttonStyle={ButtonStyle.light}
                    tooltip={
                      conference.hasEnded
                        ? 'This conference has ended'
                        : 'Enabling this conference turns on invites and sign-ups.'
                    }
                    onClick={() => activateConference(conference.id, true)}
                    disabled={conference.hasEnded}
                  >
                    Turn On Signups
                  </Button>
                ))}
              {user.PTCAdmin && (
                <Button
                  buttonStyle={ButtonStyle.light}
                  disabled={!conference.canBeEdited}
                  tooltip={conference.canBeEdited ? undefined : 'A conference must have no appointments to be edited.'}
                  link={`/ptc/editConference/${id}`}
                >
                  Edit
                </Button>
              )}
            </div>
            <Tabs className="mt-2">
              <Tab tab="schedule" label="Schedule">
                <ScheduleTab
                  conference={conference}
                  viewAsAdmin={viewAsAdmin}
                  user={user}
                  editYourLocation={() => setEditingLocation(true)}
                />
              </Tab>
              <Tab
                tab="invite"
                label={
                  viewAsAdmin && conference.adminLed ? 'Mass Invites' : conference.adminLed ? 'All Invites' : 'Invite'
                }
              >
                <InviteTab
                  conference={conference}
                  viewAsAdmin={viewAsAdmin}
                  user={user}
                  editYourLocation={() => setEditingLocation(true)}
                />
              </Tab>
              <Tab tab="appointments" label={viewAsAdmin ? 'All Appointments' : 'My Appointments'}>
                <AppointmentsTab
                  conference={conference}
                  viewAsAdmin={viewAsAdmin}
                  user={user}
                  editYourLocation={() => setEditingLocation(true)}
                />
              </Tab>
              {viewAsAdmin ? (
                <>
                  <Tab tab="teacherInfo" label="All Teacher Info">
                    <TeacherInfoTab conferenceID={conference.id} user={user} />
                  </Tab>
                  <Tab tab="studentsInfo" label="All Students Info">
                    <StudentInfoTab conference={conference} user={user} />
                  </Tab>
                  <Tab tab="statistics" label="Statistics">
                    <StatsTab conference={conference} user={user} />
                  </Tab>
                </>
              ) : (
                <Tab tab="otherAppointments" label="Other Appointments">
                  <AppointmentsTab
                    conference={conference}
                    viewAsAdmin={viewAsAdmin}
                    user={user}
                    editYourLocation={() => setEditingLocation(true)}
                    showOtherTeachersAppointments
                  />
                </Tab>
              )}
            </Tabs>
          </>
        ) : (
          <Loader />
        )}
      </div>
    </div>
  )
}
